import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, interval, BehaviorSubject, throwError } from 'rxjs';
import { takeUntil, switchMap, tap, catchError, filter, take } from 'rxjs/operators';
import { RecipientRequestService, RecipientRequestStatus } from '../../communication/index';

/**
 * Signing status values returned from the server
 */
export enum SigningStatus {
  SUCCESS = 'SUCCESS',
  REJECTED = 'REJECTED',
  WAITING = 'WAITING',
  CERTIFICATE_EXPIRING_SOON = 'CERTIFICATE_EXPIRING_SOON',
  SIGNING_ERROR = 'SIGNING_ERROR',
  NAME_NOT_MATCH = 'NAME_NOT_MATCH',
  CITIZEN_ID_NOT_MATCH = 'CITIZEN_ID_NOT_MATCH'
}

/**
 * Result of a status poll
 */
export interface StatusPollResult {
  status: SigningStatus | string;
  isComplete: boolean;
  isSuccess: boolean;
  isRejected: boolean;
  isError: boolean;
  errorMessage?: string;
  /**
   * Numeric ResponseMessage code of a recorded signing failure, surfaced
   * by the backend on the status poll (recipientRequestStatus.errorCode, string e.g.
   * "304"). Present only on failure — notably the async mobile-signing (msign) path
   * whose failure surfaces here rather than in the /msign response. Localize by code.
   */
  errorCode?: string;
  /** Human-readable fallback description of the recorded failure. */
  errorDescription?: string;
}

/**
 * Configuration for status polling
 */
export interface PollingConfig {
  /** Interval between status checks in milliseconds (default: 2000ms) */
  intervalMs: number;
  /** Maximum number of poll attempts before timeout (default: 75 = 150 seconds) */
  maxAttempts: number;
}

/**
 * Service for polling the signing status during mobile signing.
 *
 * After initiating a mobile signature, the system needs to poll
 * the server to check when the user has completed signing on their device.
 *
 * Status values:
 * - SUCCESS: Signing completed successfully
 * - REJECTED: User rejected the signing request
 * - WAITING: Still waiting for user action (continue polling)
 * - CERTIFICATE_EXPIRING_SOON: Certificate is about to expire
 * - SIGNING_ERROR, NAME_NOT_MATCH, CITIZEN_ID_NOT_MATCH: Error states
 */
@Injectable({
  providedIn: 'root'
})
export class SigningStatusService {

  /** Default polling configuration */
  private readonly defaultConfig: PollingConfig = {
    intervalMs: 2000,  // 2 seconds
    maxAttempts: 75    // 75 * 2 = 150 seconds total timeout
  };

  /** Current polling subscription */
  private pollingSubscription: Subscription | null = null;

  /** Subject to stop polling */
  private stopPolling$ = new Subject<void>();

  /** Current polling state */
  private isPolling$ = new BehaviorSubject<boolean>(false);

  /** Track consecutive errors */
  private errorCount = 0;
  private readonly maxConsecutiveErrors = 2;

  constructor(private recipientRequestApi: RecipientRequestService) {}

  /**
   * Get observable for polling state
   */
  getPollingState(): Observable<boolean> {
    return this.isPolling$.asObservable();
  }

  /**
   * Check if currently polling
   */
  isPolling(): boolean {
    return this.isPolling$.value;
  }

  /**
   * Start polling for signing status.
   *
   * @param requestCode The recipient request code
   * @param config Optional polling configuration
   * @returns Observable that emits StatusPollResult on each poll
   */
  startPolling(requestCode: string, config?: Partial<PollingConfig>): Observable<StatusPollResult> {
    const mergedConfig = { ...this.defaultConfig, ...config };

    // Stop any existing polling
    this.stopPolling();

    this.isPolling$.next(true);
    this.errorCount = 0;

    let attemptCount = 0;

    return new Observable<StatusPollResult>(observer => {
      this.pollingSubscription = interval(mergedConfig.intervalMs).pipe(
        takeUntil(this.stopPolling$),
        tap(() => {
          attemptCount++;
          if (attemptCount >= mergedConfig.maxAttempts) {
            observer.next({
              status: 'TIMEOUT',
              isComplete: true,
              isSuccess: false,
              isRejected: false,
              isError: true,
              errorMessage: 'Signing timeout - no response from device'
            });
            this.stopPolling();
          }
        }),
        filter(() => attemptCount < mergedConfig.maxAttempts),
        switchMap(() => this.recipientRequestApi.recipientRequestsCodeStatusGet(requestCode))
      ).subscribe({
        next: (response: RecipientRequestStatus) => {
          this.errorCount = 0; // Reset error count on success
          const result = this.parseStatusResponse(response.status);
          // Carry the backend's failure code so the component can map it
          // (e.g. 304 TIMESTAMP_SERVICE_UNAVAILABLE for the msign flow).
          result.errorCode = response.errorCode;
          result.errorDescription = response.errorDescription;
          observer.next(result);

          if (result.isComplete) {
            this.stopPolling();
            observer.complete();
          }
        },
        error: (err) => {
          this.errorCount++;
          console.error('Status polling error:', err);

          if (this.errorCount >= this.maxConsecutiveErrors) {
            observer.next({
              status: 'NETWORK_ERROR',
              isComplete: true,
              isSuccess: false,
              isRejected: false,
              isError: true,
              errorMessage: 'Network error during status polling'
            });
            this.stopPolling();
            observer.complete();
          }
        },
        complete: () => {
          this.isPolling$.next(false);
          observer.complete();
        }
      });

      // Return cleanup function
      return () => {
        this.stopPolling();
      };
    });
  }

  /**
   * Stop the current polling operation
   */
  stopPolling(): void {
    this.stopPolling$.next();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    this.isPolling$.next(false);
  }

  /**
   * Pause polling temporarily (e.g., when modal is minimized)
   * Note: This doesn't actually pause, but the component can check isPaused
   * before processing results
   */
  private _isPaused = false;

  pausePolling(): void {
    this._isPaused = true;
  }

  resumePolling(): void {
    this._isPaused = false;
  }

  isPaused(): boolean {
    return this._isPaused;
  }

  /**
   * Get a single status check (no polling)
   *
   * @param requestCode The recipient request code
   * @returns Observable with single status result
   */
  checkStatus(requestCode: string): Observable<StatusPollResult> {
    return this.recipientRequestApi.recipientRequestsCodeStatusGet(requestCode).pipe(
      map((response: RecipientRequestStatus) => {
        const result = this.parseStatusResponse(response.status);
        result.errorCode = response.errorCode;
        result.errorDescription = response.errorDescription;
        return result;
      }),
      catchError((err) => {
        return throwError(() => ({
          status: 'NETWORK_ERROR',
          isComplete: true,
          isSuccess: false,
          isRejected: false,
          isError: true,
          errorMessage: err?.message || 'Network error checking status'
        } as StatusPollResult));
      })
    );
  }

  /**
   * Parse the status string into a structured result
   *
   * @param status Status string from server
   * @returns Parsed status result
   */
  private parseStatusResponse(status: string): StatusPollResult {
    const normalizedStatus = status?.toUpperCase() || '';

    switch (normalizedStatus) {
      case SigningStatus.SUCCESS:
        return {
          status: SigningStatus.SUCCESS,
          isComplete: true,
          isSuccess: true,
          isRejected: false,
          isError: false
        };

      case SigningStatus.REJECTED:
        return {
          status: SigningStatus.REJECTED,
          isComplete: true,
          isSuccess: false,
          isRejected: true,
          isError: false
        };

      case SigningStatus.WAITING:
        return {
          status: SigningStatus.WAITING,
          isComplete: false,
          isSuccess: false,
          isRejected: false,
          isError: false
        };

      case SigningStatus.CERTIFICATE_EXPIRING_SOON:
        return {
          status: SigningStatus.CERTIFICATE_EXPIRING_SOON,
          isComplete: true,
          isSuccess: false,
          isRejected: false,
          isError: true,
          errorMessage: 'Certificate is expiring soon'
        };

      case SigningStatus.SIGNING_ERROR:
      case SigningStatus.NAME_NOT_MATCH:
      case SigningStatus.CITIZEN_ID_NOT_MATCH:
        return {
          status: normalizedStatus as SigningStatus,
          isComplete: true,
          isSuccess: false,
          isRejected: false,
          isError: true,
          errorMessage: `Signing error: ${normalizedStatus}`
        };

      default:
        // Unknown status - treat as error if not WAITING-like
        if (normalizedStatus.includes('WAIT') || normalizedStatus.includes('PENDING')) {
          return {
            status: normalizedStatus,
            isComplete: false,
            isSuccess: false,
            isRejected: false,
            isError: false
          };
        }
        return {
          status: normalizedStatus,
          isComplete: true,
          isSuccess: false,
          isRejected: false,
          isError: true,
          errorMessage: `Unknown signing status: ${normalizedStatus}`
        };
    }
  }

  /**
   * Check if a status indicates the signing is complete
   */
  isStatusComplete(status: string): boolean {
    return this.parseStatusResponse(status).isComplete;
  }

  /**
   * Check if a status indicates success
   */
  isStatusSuccess(status: string): boolean {
    return this.parseStatusResponse(status).isSuccess;
  }
}

// Import map operator that was used
import { map } from 'rxjs/operators';
